import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext, useWatch } from 'react-hook-form';

import { formatPrice } from '@klareospace/pharos-lib-front-utils';
import { joinStrings } from '@klareospace/pharos-lib-utils';
import { StepperAccordionSummary } from '@klareospace/pharos-lib-web-utils';
import type { StepperAccordionSummaryItem } from '@klareospace/pharos-lib-web-utils';

import { isPositive } from '../hooks';
import type { OrderCreationFormInput } from '../schemas/order-creation.schema';

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Collapsed "Content" section recap. Owns its own `useWatch` on `content` so a Content keystroke
 * only re-renders this leaf instead of the whole drawer (and every other section's summary).
 */
export function ContentSectionSummary(): React.JSX.Element {
  const { i18n, t: tCommon } = useTranslation('common');
  const { t } = useTranslation('order-creation');
  const { control } = useFormContext<OrderCreationFormInput>();
  const content = useWatch({ control, name: 'content' });

  // Each item is only included when its backing value is actually filled — mirrors
  // `buildCustomerSummaryItems`' "no Label: with a blank value" rule. Quantity and handling-unit
  // count are independent (both shown when both are present), not an either/or choice. Kept as a
  // closure (not a top-level helper) so the i18next-no-undefined-translation-keys ESLint rule can
  // resolve `t`'s namespace from the enclosing `useTranslation` call.
  function buildContentSummaryItems(): StepperAccordionSummaryItem[] {
    const items: StepperAccordionSummaryItem[] = [];
    if (isPositive(content.totalQuantity)) {
      items.push({ label: t('sections.content.summary.quantity'), value: String(Number(content.totalQuantity)) });
    }
    if (content.handlingUnits.length > 0) {
      items.push({
        label: t('sections.content.fields.handlingUnits.label'),
        value: t('sections.content.summary.handlingUnits', { count: content.handlingUnits.length }),
      });
    }
    if (content.totalWeight.trim() !== '') {
      items.push({
        label: t('sections.content.summary.weight'),
        value: `${content.totalWeight} ${tCommon(`units.${content.weightUnit}`)}`,
      });
    }
    if (content.totalVolume.trim() !== '') {
      items.push({
        label: t('sections.content.summary.volume'),
        value: `${content.totalVolume} ${tCommon(`units.${content.volumeUnit}`)}`,
      });
    }
    if (content.orderValue.trim() !== '') {
      items.push({
        label: t('sections.content.summary.orderValue'),
        value: formatPrice({ currency: content.currency, value: Number(content.orderValue) }, i18n.language),
      });
    }
    if (content.tags.length > 0) {
      items.push({ label: t('sections.content.summary.tags'), value: joinStrings(content.tags) });
    }
    return items;
  }

  return <StepperAccordionSummary items={buildContentSummaryItems()} />;
}
